import { Box, Chip, Typography } from "@mui/material";
import { settings } from "app";
import EditIcon from "@mui/icons-material/Edit";
import {
  Button,
  Datagrid,
  FunctionField,
  NumberField,
  ReferenceField,
  SelectField,
  TextField,
  useRecordContext,
} from "react-admin";
import permissions from "app/permissions";
import SecuredComponent from "server/security/SecuredComponent";
import { SectionTitle } from "features/SectionTitle";
import { administrationRequests } from "api/administrationApi";
import { NationalityType } from "utils/choices";
import NestedItems from "./NestedItems";
import RowActions from "./RowActions";
import SubCyclesSection from "./SubCyclesSection";
import { useCollectionActions } from "./CollectionContext";
import CreditFields from "../forms/CreditFields";
import CourseYearsDialog from "../dialogs/CourseYearsDialog";

// Keeps the course years, credits and sub-cycles of a cycle visually apart.
const CycleSubSection = ({ children }) => (
  <Box
    sx={{
      mt: 2,
      p: 2,
      border: 1,
      borderColor: "divider",
      borderRadius: 1,
    }}
  >
    {children}
  </Box>
);

const CourseYears = ({ cycleIndex }) => {
  const { items, saving, updateNestedAt } = useCollectionActions();

  const schedules = items[cycleIndex]?.schedules ?? [];
  const years = [...new Set(schedules.map(schedule => schedule.year).filter(year => year != null))].sort(
    (a, b) => a - b,
  );

  // Reuses the saved entry of a year that stays ticked, so nothing else on it is lost.
  const saveYears = nextYears =>
    updateNestedAt(
      cycleIndex,
      "schedules",
      nextYears.map(year => schedules.find(schedule => schedule.year === year) ?? { year }),
    );

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <SectionTitle
          title="Курс"
          paddingY={0}
          color="secondary"
        />
        <SecuredComponent permission={permissions.canUpdateCurriculums}>
          <CourseYearsDialog
            years={years}
            onSave={saveYears}
            triggerButton={
              <Button
                label="Редакция"
                startIcon={<EditIcon />}
                size="small"
                disabled={saving}
              />
            }
          />
        </SecuredComponent>
      </Box>

      {years.length ? (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
          {years.map(year => (
            <Chip
              key={year}
              label={year}
              size="small"
            />
          ))}
        </Box>
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
        >
          Няма добавени курсове.
        </Typography>
      )}
    </>
  );
};

const CyclicExpand = () => {
  const row = useRecordContext();
  const { items, saving, updateNestedAt } = useCollectionActions();

  if (!row) return null;

  const item = items[row.rowIndex];

  return (
    <Box sx={{ p: 1 }}>
      <CycleSubSection>
        <CourseYears cycleIndex={row.rowIndex} />
      </CycleSubSection>

      <CycleSubSection>
        <NestedItems
          title="Кредити"
          singular="кредити"
          rowPrefix={`cyclicDisciplines#${row.rowIndex}.credits`}
          items={item?.credits}
          FormFields={CreditFields}
          saving={saving}
          onSave={next => updateNestedAt(row.rowIndex, "credits", next)}
        >
          <SelectField
            source="nationality"
            choices={NationalityType}
            label="Националност"
            sortable={false}
            emptyText="-"
          />
          <ReferenceField
            source="instructionLanguageId"
            reference={administrationRequests.getInstructionLanguagesSelect()}
            label="Език на преподаване"
            link={false}
            sortable={false}
            emptyText="-"
          >
            <TextField
              source="name"
              emptyText="-"
            />
          </ReferenceField>
          <NumberField
            source="credits"
            label="Кредити"
            sortable={false}
            textAlign="left"
            emptyText="-"
          />
        </NestedItems>
      </CycleSubSection>

      <CycleSubSection>
        <SubCyclesSection cycleIndex={row.rowIndex} />
      </CycleSubSection>
    </Box>
  );
};

const CyclicDisciplinesGrid = ({ collection }) => (
  <Datagrid
    bulkActionButtons={false}
    rowClick={false}
    expandSingle
    expand={<CyclicExpand />}
    sx={[settings.style.detailsDatagrid, { "& .RaDatagrid-headerCell": { textAlign: "left" } }]}
  >
    <ReferenceField
      source="disciplineId"
      reference={administrationRequests.getDisciplinesSelect()}
      label="Цикъл"
      link={false}
      sortable={false}
      emptyText="-"
    >
      <TextField
        source="name"
        emptyText="-"
      />
    </ReferenceField>
    <NumberField
      source="totalHours"
      label="Общо часове"
      sortable={false}
      textAlign="left"
      emptyText="-"
    />
    <NumberField
      source="totalLectures"
      label="Общо лекции"
      sortable={false}
      textAlign="left"
      emptyText="-"
    />
    <NumberField
      source="totalLabs"
      label="Общо упражнения"
      sortable={false}
      textAlign="left"
      emptyText="-"
    />
    <NumberField
      source="durationDays"
      label="Учебни дни"
      sortable={false}
      textAlign="left"
      emptyText="-"
    />
    <NumberField
      source="extracurricularActivities"
      label="Извънаудиторна"
      sortable={false}
      textAlign="left"
      emptyText="-"
    />
    <FunctionField
      label=""
      sortable={false}
      render={() => <RowActions collection={collection} />}
    />
  </Datagrid>
);

export default CyclicDisciplinesGrid;
